import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import produce from 'immer';
import React, {useCallback, useEffect, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import * as Analytics from 'analytics/Analytics';
import {NormalizedAnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import {standardAnalyticsMetadataToDict} from 'analytics/Util';
import {assert} from 'Asserts';
import {BenchlingExpressionType} from 'benchling-expressions/BenchlingExpressionTypes';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  AnalyticsMetadataForContext,
  COMMON_ANALYTICS_ACTIONS,
  WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import notifications from 'notifications';
import getSchemaOfItemsBeingRouted from 'processes/graph-builder-processes/flowchart-viz/util/getSchemaOfItemsBeingRouted';
import {
  ProcessFlowchartConfigForGraphBuilder,
  ProcessGraphBuilderNode,
  ProcessGraphBuilderRootNode,
  ProcessGraphBuilderTaskNode,
  ProcessTaskSchemaForGraphBuilderValidation,
} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import ConditionalLogicConfig from 'processes/modals/router-config/ConditionalLogicConfig';
import LeaveRouterConfigConfirmationModal from 'processes/modals/router-config/LeaveRouterConfigConfirmationModal';
import {
  wasInitialRouterConfigUnconfigured,
  wasRouterConfigUpdated,
  wereRouterConfigFunctionsUpdated,
} from 'processes/modals/router-config/RouterConfigUtils';
import {validateAllFunctionSpecs} from 'processes/modals/router-config/RouterConfigValidation';
import {useAllSchemasById} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {Schema} from 'schemas/SchemaConstants';
import InlineBanner, {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import APIUtil from 'util/APIUtil';

import {ProcessOutputSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessOutputSchemaGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  CreateOrUpdateRouterFunctionSpec,
  CreateRouterFunctionSpec,
  DEFAULT_DEFAULT_FUNCTION_NAME,
  DEFAULT_FUNCTION_NAME,
  DELETE_ROUTER_CONFIRMATION_MODAL_MESSAGE,
  DELETE_ROUTER_CONFIRMATION_MODAL_TITLE,
  ROUTER_CONFIGURATION_MODAL_TYPE,
  ROUTER_ERROR_TYPES,
  RouterConfig,
  sharedStyles,
  UpdateRouterFunctionSpec,
} from './RouterConfigConstants';

const MODAL_TITLE = 'Edit router';

export interface RouterConfigModalProps {
  routerConfig: RouterConfig;
  routerNodeClientId: string;
  inputNodes: Array<ProcessGraphBuilderNode>;
  flowchartConfig: ProcessFlowchartConfigForGraphBuilder;
  flowchartClientId: string;
  flowchartSchema: ProcessTaskSchemaForGraphBuilderValidation;
  didInitialFlowchartHaveChainedRouters: boolean;
  doesCurrentFlowchartHaveChainedRouters: boolean;
  onUpdateRouterConfig: (newRouterDetails: RouterConfig) => void;
  onRequestClose: () => void;
  onDelete: () => void;
  /** All task nodes the router can flow information into */
  availableDownstreamTaskNodes: Array<ProcessGraphBuilderTaskNode>;
  /** Child nodes of the router that are directly connected to the router */
  immediateChildNodes: Array<ProcessGraphBuilderNode>;
  analyticsMetadata: AnalyticsMetadataForContext;
}

function requestClose(
  onRequestClose: () => void,
  routerConfig: RouterConfig,
  name: string,
  defaultFunctionSpec: CreateOrUpdateRouterFunctionSpec | null,
  functionSpecs: Array<CreateOrUpdateRouterFunctionSpec>,
  analyticsMetadataDict: NormalizedAnalyticsEventMetadata
): void {
  if (wasRouterConfigUpdated(routerConfig, name, defaultFunctionSpec, functionSpecs)) {
    legacyRunModal(LeaveRouterConfigConfirmationModal, {
      onLeave: () => {
        Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
          ...analyticsMetadataDict,
          modal_type: ROUTER_CONFIGURATION_MODAL_TYPE,
        });
        onRequestClose();
      },
    });
  } else {
    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
      ...analyticsMetadataDict,
      modal_type: ROUTER_CONFIGURATION_MODAL_TYPE,
    });
    onRequestClose();
  }
}

interface RouterConfigFormState {
  inputNode: ProcessGraphBuilderTaskNode | ProcessGraphBuilderRootNode;
  name: string;
  setName: (name: string) => void;
  isRouterNameBeingEdited: boolean;
  setIsRouterNameBeingEdited: (isRouterNameBeingEdited: boolean) => void;
  routerFunctionNameEditStatus: Array<boolean>;
  setRouterFunctionNameEditStatus: (routerFunctionNameEditStatus: Array<boolean>) => void;
  defaultFunctionSpec: CreateOrUpdateRouterFunctionSpec;
  setDefaultFunctionSpec: (defaultFunctionSpec: CreateOrUpdateRouterFunctionSpec) => void;
  functionSpecs: Array<CreateOrUpdateRouterFunctionSpec>;
  setFunctionSpecs: (functionSpecs: Array<CreateOrUpdateRouterFunctionSpec>) => void;
  isSpecValidMessage: string | null;
  duplicateNextNodeFunctions: Set<string>;
  setDuplicateNextNodeFunctions: (duplicateNextNodeFunctions: Set<string>) => void;
  setIsSpecValidMessage: (isSpecValidMessage: string | null) => void;
  bannerDetails: InlineBannerProps | null;
  setBannerDetails: (bannerDetails: InlineBannerProps | null) => void;
  allSchemasById: SchemaById;
  allSchemasQueryResultType: QueryResultType;
  analyticsMetadataDict: NormalizedAnalyticsEventMetadata;
  submitDisabledMessage: string | null;
}

export const useRouterConfigForm = (
  props: Pick<
    RouterConfigModalProps,
    'routerConfig' | 'inputNodes' | 'analyticsMetadata' | 'flowchartSchema' | 'routerNodeClientId'
  >
): RouterConfigFormState => {
  assert(props.inputNodes.length === 1, 'A router node must have exactly one input node');
  const inputNode = props.inputNodes[0];
  assert(
    inputNode.type !== ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
    'Input node of router cannot be a flowchart output node'
  );
  assert(
    inputNode.type !== ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
    'Input node of router cannot be a router node'
  );
  assert(
    inputNode.type !== ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
    'Input node of router cannot be an end node'
  );

  const [name, setName] = useState(props.routerConfig.name);
  const [isRouterNameBeingEdited, setIsRouterNameBeingEdited] = useState(false);
  const [routerFunctionNameEditStatus, setRouterFunctionNameEditStatus] = useState(
    new Array(props.routerConfig.functionSpecs.length + 1).fill(false)
  );

  const getDefaultFunctionSpecFromRouterConfig = useCallback(
    (routerConfig: RouterConfig): CreateOrUpdateRouterFunctionSpec =>
      routerConfig.defaultFunctionSpec
        ? ({
            ...routerConfig.defaultFunctionSpec,
            isDefault: true,
            isConfigured: true,
          } as UpdateRouterFunctionSpec)
        : ({
            clientId: uuidv4(),
            name: DEFAULT_DEFAULT_FUNCTION_NAME,
            isDefault: true,
            isConfigured: false,
          } as CreateRouterFunctionSpec),
    []
  );

  const getFunctionSpecsFromRouterConfig = useCallback(
    (routerConfig: RouterConfig): Array<CreateOrUpdateRouterFunctionSpec> =>
      routerConfig.functionSpecs.length > 0
        ? routerConfig.functionSpecs.map(
            (functionSpec) =>
              ({
                ...functionSpec,
                isConfigured: true,
                expression: functionSpec.expression ?? {type: BenchlingExpressionType.FUNCTION},
              }) as CreateOrUpdateRouterFunctionSpec
          )
        : [
            {
              clientId: uuidv4(),
              name: DEFAULT_FUNCTION_NAME,
              isDefault: false,
              expression: {
                type: BenchlingExpressionType.FUNCTION,
              },
              isConfigured: false,
            },
          ],
    []
  );

  const [defaultFunctionSpec, setDefaultFunctionSpec] = useState(
    getDefaultFunctionSpecFromRouterConfig(props.routerConfig)
  );
  const [functionSpecs, setFunctionSpecs] = useState<Array<CreateOrUpdateRouterFunctionSpec>>(
    getFunctionSpecsFromRouterConfig(props.routerConfig)
  );

  useEffect(() => {
    const newFunctionSpecsFromProps = getFunctionSpecsFromRouterConfig(props.routerConfig);
    setFunctionSpecs((currentSpecs) => {
      const propsClientIds = new Set(newFunctionSpecsFromProps.map((spec) => spec.clientId));
      const currentClientIds = new Set(currentSpecs.map((spec) => spec.clientId));

      const specsToKeep = currentSpecs.filter((spec) => propsClientIds.has(spec.clientId));
      const specsToAdd = newFunctionSpecsFromProps.filter((spec) => !currentClientIds.has(spec.clientId));

      return [...specsToKeep, ...specsToAdd];
    });

    const newDefaultFromProps = getDefaultFunctionSpecFromRouterConfig(props.routerConfig);
    setDefaultFunctionSpec((currentDefault) => {
      if (!currentDefault.isConfigured) {
        return newDefaultFromProps;
      }
      return currentDefault;
    });
  }, [
    props.routerNodeClientId,
    props.routerConfig,
    getFunctionSpecsFromRouterConfig,
    getDefaultFunctionSpecFromRouterConfig,
  ]);

  const [isSpecValidMessage, setIsSpecValidMessage] = useState<string | null>(null);
  const [bannerDetails, setBannerDetails] = useState<InlineBannerProps | null>(null);
  const [duplicateNextNodeFunctions, setDuplicateNextNodeFunctions] = useState(new Set<string>());
  const {allSchemasById, allSchemasQueryResultType} = useAllSchemasById();
  const analyticsMetadataDict = standardAnalyticsMetadataToDict(props.analyticsMetadata);

  useEffect(() => {
    if (allSchemasQueryResultType !== QueryResultType.SUCCESS) {
      return;
    }
    const validationResult = validateAllFunctionSpecs(defaultFunctionSpec, functionSpecs, allSchemasById);
    setIsSpecValidMessage(validationResult?.errorMessage ?? null);

    if (
      validationResult != null &&
      validationResult.errorType === ROUTER_ERROR_TYPES.DUPLICATED_NODE_ERROR_TYPE &&
      validationResult.duplicatedNodes != null
    ) {
      setDuplicateNextNodeFunctions(validationResult.duplicatedNodes);
    } else {
      setDuplicateNextNodeFunctions(new Set<string>());
    }

    if (validationResult == null || !validationResult.isBannerError) {
      setBannerDetails(null);
    } else {
      if (bannerDetails == null) {
        Analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.ERROR_MESSAGE_SHOWN_ROUTER_CONFIGURATION, {
          ...analyticsMetadataDict,
          action_origin: ActionOrigin.WORKFLOWS_ROUTER_NODE_CONFIGURATION_MODAL,
          process_client_origin: props.flowchartSchema.clientOrigin,
          error_type: validationResult.errorType,
          node_id: props.routerNodeClientId,
        });
      }

      setBannerDetails({
        bannerType: InlineBannerType.ERROR,
        hideIcon: false,
        text: validationResult.errorMessage,
        supportingInfo: validationResult.supportingInfo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFunctionSpec, functionSpecs, allSchemasQueryResultType]);

  function submitIsDisabledWithMessage(): string | null {
    if (isSpecValidMessage != null) {
      return isSpecValidMessage;
    } else if (isRouterNameBeingEdited) {
      return 'The name of the router is still being edited.';
    } else if (routerFunctionNameEditStatus.some((isEdgeLabelBeingEdited) => isEdgeLabelBeingEdited)) {
      return 'The name of one or more of the router routes are still being edited.';
    }
    return null;
  }

  const submitDisabledMessage = submitIsDisabledWithMessage();

  return {
    inputNode,
    name,
    setName,
    isRouterNameBeingEdited,
    setIsRouterNameBeingEdited,
    routerFunctionNameEditStatus,
    setRouterFunctionNameEditStatus,
    defaultFunctionSpec,
    setDefaultFunctionSpec,
    functionSpecs,
    setFunctionSpecs,
    isSpecValidMessage,
    setIsSpecValidMessage,
    bannerDetails,
    setBannerDetails,
    duplicateNextNodeFunctions,
    setDuplicateNextNodeFunctions,
    allSchemasById,
    allSchemasQueryResultType,
    analyticsMetadataDict,
    submitDisabledMessage,
  };
};

type RouterConfigFormBaseProps = Pick<
  RouterConfigModalProps,
  | 'routerNodeClientId'
  | 'flowchartClientId'
  | 'analyticsMetadata'
  | 'immediateChildNodes'
  | 'didInitialFlowchartHaveChainedRouters'
  | 'doesCurrentFlowchartHaveChainedRouters'
>;

export function RouterConfigForm(
  props: RouterConfigFormBaseProps &
    RouterConfigFormState & {
      flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
      inputSchema: Schema;
      inputNode: ProcessGraphBuilderTaskNode | ProcessGraphBuilderRootNode;
      showDisabledMessageAsBanner?: boolean;
    }
): JSX.Element | null {
  const {
    inputNode,
    defaultFunctionSpec,
    setDefaultFunctionSpec,
    functionSpecs,
    setFunctionSpecs,
    bannerDetails,
    duplicateNextNodeFunctions,
    setIsRouterNameBeingEdited,
    routerFunctionNameEditStatus,
    setRouterFunctionNameEditStatus,
    name,
    setName,
    flowchartTaskSchema,
    inputSchema,
    submitDisabledMessage,
    showDisabledMessageAsBanner = false,
  } = props;

  return (
    <div className={css(sharedStyles.routerConfigFormContainer)}>
      <div className={css(styles.inputNodeBanner)}>
        Input node:
        <Chip text={inputNode.name} size={ChipSize.MINI} />
      </div>
      {showDisabledMessageAsBanner && submitDisabledMessage && (
        <div className={css(styles.section, styles.disabledMessageBanner)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="The router configuration has not been saved"
            supportingInfo={submitDisabledMessage}
          />
        </div>
      )}
      <div className={classnames(css(sharedStyles.routerOrRouteName), css(styles.section))}>
        <EditableTextField
          requestChange={(changedValue) => {
            setName(String(changedValue));
          }}
          onIsBeingEditedChanged={setIsRouterNameBeingEdited}
          value={name}
          dataTestAttrs={{component: 'RouterConfigModal', element: 'routerName'}}
        />
      </div>
      {bannerDetails && (
        <div className={css(styles.errorBanner)}>
          <InlineBanner {...bannerDetails} />
        </div>
      )}
      {functionSpecs.map((routerFunction, index) => (
        <ConditionalLogicConfig
          routerNodeClientId={props.routerNodeClientId}
          flowchartClientId={props.flowchartClientId}
          key={index}
          functionSpec={routerFunction as CreateRouterFunctionSpec}
          setFunctionSpec={(newFunction) => {
            setFunctionSpecs(
              produce(functionSpecs, (draftSpecs) => {
                draftSpecs[index] = newFunction;
              })
            );
          }}
          onIsRouterFunctionNameBeingEdited={(isBeingEdited) => {
            setRouterFunctionNameEditStatus(
              produce(routerFunctionNameEditStatus, (draftRouterFunctionNameEditStatus) => {
                draftRouterFunctionNameEditStatus[index] = isBeingEdited;
              })
            );
          }}
          inputNode={inputNode}
          inputSchema={
            inputSchema as ProcessTaskSchemaWithoutFlowchartConfigGraphQL | ProcessOutputSchemaGraphQL
          }
          flowchartTaskSchema={flowchartTaskSchema}
          immediateChildNodes={props.immediateChildNodes}
          didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
          doesCurrentFlowchartHaveChainedRouters={props.doesCurrentFlowchartHaveChainedRouters}
          analyticsMetadata={props.analyticsMetadata}
          dataTestAttrs={{component: 'RouterConfigModal', element: 'conditionalLogic', key: `${index}`}}
          errors={{isNextNodeDuplicate: duplicateNextNodeFunctions.has(routerFunction.clientId)}}
        />
      ))}
      <ConditionalLogicConfig
        routerNodeClientId={props.routerNodeClientId}
        flowchartClientId={props.flowchartClientId}
        functionSpec={defaultFunctionSpec as CreateRouterFunctionSpec}
        setFunctionSpec={setDefaultFunctionSpec}
        onIsRouterFunctionNameBeingEdited={(isBeingEdited) => {
          setRouterFunctionNameEditStatus(
            produce(routerFunctionNameEditStatus, (draftRouterFunctionNameEditStatus) => {
              draftRouterFunctionNameEditStatus[draftRouterFunctionNameEditStatus.length - 1] = isBeingEdited;
            })
          );
        }}
        inputNode={inputNode}
        inputSchema={
          inputSchema as ProcessTaskSchemaWithoutFlowchartConfigGraphQL | ProcessOutputSchemaGraphQL
        }
        flowchartTaskSchema={flowchartTaskSchema}
        immediateChildNodes={props.immediateChildNodes}
        didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
        doesCurrentFlowchartHaveChainedRouters={props.doesCurrentFlowchartHaveChainedRouters}
        analyticsMetadata={props.analyticsMetadata}
        dataTestAttrs={{component: 'RouterConfigModal', element: 'defaultConditionalLogic'}}
        errors={{isNextNodeDuplicate: duplicateNextNodeFunctions.has(defaultFunctionSpec.clientId)}}
      />
    </div>
  );
}

export default function RouterConfigModal(props: RouterConfigModalProps): JSX.Element | null {
  const routerConfigFormState = useRouterConfigForm(props);
  const {
    inputNode,
    name,
    defaultFunctionSpec,
    functionSpecs,
    analyticsMetadataDict,
    allSchemasById,
    allSchemasQueryResultType,
    submitDisabledMessage,
  } = routerConfigFormState;

  if (allSchemasQueryResultType === QueryResultType.ERROR) {
    notifications.error('Could not load available schemas; try reloading the page.');
    return null;
  } else if (allSchemasQueryResultType === QueryResultType.PENDING) {
    return (
      <Modal onRequestClose={props.onRequestClose} title={MODAL_TITLE}>
        <CenteredHorizontalThrobber />
      </Modal>
    );
  }
  const inputSchemaId = getSchemaOfItemsBeingRouted(
    inputNode,
    props.availableDownstreamTaskNodes,
    props.flowchartConfig.schemaMapperConfigs
  );
  if (inputSchemaId == null) {
    notifications.error(
      'Router could not be configured because there are no valid mappers between the upstream and downstream nodes.'
    );
    return null;
  }
  assert(
    APIUtil.apiIdToMaybeType(inputSchemaId) === ModelTypeConstants.PROCESS_TASK_SCHEMA ||
      APIUtil.apiIdToMaybeType(inputSchemaId) === ModelTypeConstants.PROCESS_OUTPUT_SCHEMA,
    'Input schema must be a process task or output schema'
  );
  const inputSchema = allSchemasById[inputSchemaId];
  assert(inputSchema != null, `Could not find input schema with ID ${inputSchemaId}`);

  const flowchartTaskSchema = allSchemasById[props.flowchartSchema.id] as ProcessTaskSchemaGraphQL;
  assert(flowchartTaskSchema != null, 'Could not find flowchart task schema');

  return (
    <Modal
      title={MODAL_TITLE}
      onRequestClose={() => {
        requestClose(
          props.onRequestClose,
          props.routerConfig,
          routerConfigFormState.name,
          routerConfigFormState.defaultFunctionSpec,
          routerConfigFormState.functionSpecs,
          analyticsMetadataDict
        );
      }}
      onRequestSubmit={() => {
        Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
          ...analyticsMetadataDict,
          modal_type: ROUTER_CONFIGURATION_MODAL_TYPE,
        });
        if (wasRouterConfigUpdated(props.routerConfig, name, defaultFunctionSpec, functionSpecs)) {
          if (wasInitialRouterConfigUnconfigured(props.routerConfig)) {
            Analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CREATED_ROUTER_CONFIGURATION_IN_CANVAS, {
              ...analyticsMetadataDict,
              action_origin: ActionOrigin.WORKFLOWS_ROUTER_NODE_CONFIGURATION_MODAL,
              process_client_origin: props.flowchartSchema.clientOrigin,
              name,
              node_id: props.routerNodeClientId,
            });
          } else {
            Analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.UPDATED_ROUTER_CONFIGURATION_IN_CANVAS, {
              ...analyticsMetadataDict,
              action_origin: ActionOrigin.WORKFLOWS_ROUTER_NODE_CONFIGURATION_MODAL,
              updated_name: props.routerConfig.name !== name,
              updated_routes: wereRouterConfigFunctionsUpdated(
                props.routerConfig,
                defaultFunctionSpec,
                functionSpecs
              ),
              name,
              node_id: props.routerNodeClientId,
            });
          }
        }
        props.onUpdateRouterConfig({
          name,
          clientId: props.routerConfig.clientId,
          defaultFunctionSpec,
          functionSpecs,
        });
        props.onRequestClose();
      }}
      bodyPadded={false}
      footer={
        <Button
          text="Delete"
          icon="delete"
          buttonStyle={ButtonStyle.TERTIARY_WHITE}
          className={css(styles.borderless)}
          onClick={async () => {
            const confirmDelete = await runConfirmDeleteModal({
              typeName: 'router',
              title: DELETE_ROUTER_CONFIRMATION_MODAL_TITLE,
              message: DELETE_ROUTER_CONFIRMATION_MODAL_MESSAGE,
            });
            if (confirmDelete) {
              Analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.DELETED_ROUTER_CONFIGURATION_IN_CANVAS, {
                ...analyticsMetadataDict,
                action_origin: ActionOrigin.WORKFLOWS_ROUTER_NODE_CONFIGURATION_MODAL,
                name,
                node_id: props.routerNodeClientId,
              });
              props.onDelete();
              props.onRequestClose();
            }
          }}
          size={ButtonSize.MEDIUM}
          dataTestAttrs={{component: 'RouterConfigModal', element: 'deleteButton'}}
        />
      }
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          dataTestAttrs: {component: 'RouterConfigModal', element: 'cancelButton'},
        },
        {
          text: 'Save',
          disabled: submitDisabledMessage != null,
          submit: true,
          style: ButtonStyle.SECONDARY,
          tooltipTitle: submitDisabledMessage,
        },
      ]}
      dataTestAttrs={{component: 'RouterConfigModal', element: 'root'}}
      expandHeight={true}
      large={true}
    >
      <RouterConfigForm
        {...props}
        {...routerConfigFormState}
        inputNode={inputNode}
        flowchartTaskSchema={flowchartTaskSchema}
        inputSchema={inputSchema}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  borderless: {
    border: 'none',
  },
  inputNodeBanner: {
    backgroundColor: GRAY_LIGHTER,
    margin: 'none',
    paddingBlock: 8,
    paddingInline: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  section: {
    paddingBlock: 8,
    paddingInline: 16,
    display: 'flex',
  },
  errorBanner: {
    paddingInline: 0,
    margin: 16,
    marginTop: 0,
  },
  disabledMessageBanner: {
    width: '100%',
    flexDirection: 'column',
  },
});
